/*
 * Purpose: Global running-flow registry — dashboard + cross-route stop
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { create } from 'zustand';

export interface RunningFlowRecord {
  workflowId: string;
  workflowName: string;
  startedAt: string;
}

const abortByWorkflowId = new Map<string, AbortController>();

interface ExecutionRuntimeState {
  running: RunningFlowRecord[];
  registerRun: (workflowId: string, workflowName: string, controller: AbortController) => void;
  unregisterRun: (workflowId: string) => void;
  stopRun: (workflowId: string) => boolean;
  getController: (workflowId: string) => AbortController | undefined;
  runningIds: () => string[];
}

export const useExecutionRuntimeStore = create<ExecutionRuntimeState>((set, get) => ({
  running: [],

  registerRun: (workflowId, workflowName, controller) => {
    abortByWorkflowId.set(workflowId, controller);
    set((state) => {
      const filtered = state.running.filter((r) => r.workflowId !== workflowId);
      return {
        running: [
          ...filtered,
          { workflowId, workflowName, startedAt: new Date().toISOString() },
        ],
      };
    });
  },

  unregisterRun: (workflowId) => {
    abortByWorkflowId.delete(workflowId);
    set((state) => ({
      running: state.running.filter((r) => r.workflowId !== workflowId),
    }));
  },

  stopRun: (workflowId) => {
    const controller = abortByWorkflowId.get(workflowId);
    if (!controller) return false;
    controller.abort();
    abortByWorkflowId.delete(workflowId);
    set((state) => ({
      running: state.running.filter((r) => r.workflowId !== workflowId),
    }));
    return true;
  },

  getController: (workflowId) => abortByWorkflowId.get(workflowId),

  runningIds: () => get().running.map((r) => r.workflowId),
}));
